import { useCallback, useEffect, useState } from "react";
import { Power } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import { fmtDate } from "../lib/roles";
import { Panel } from "./common";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

function AutoBox() {
  const [a, setA] = useState(null);
  useEffect(() => { api("/web/auto").then(setA).catch(() => setA(false)); }, []);
  if (!a) return null;
  const save = async (patch) => {
    const next = { ...a, ...patch };
    try { setA({ ...next, ...(await api("/web/auto", { method: "PUT", body: { switch_enabled: next.switch_enabled, switch_date: next.switch_date, reminder_enabled: next.reminder_enabled } })) }); toast.success("Impostazione salvata"); } catch (e) { toast.error(e.message); }
  };
  const [y, m, g] = a.switch_date.split("-");
  return (
    <div className="rounded-xl bg-white border p-4 space-y-3" data-testid="auto-box">
      <label className="flex items-start gap-3 text-sm cursor-pointer">
        <input type="checkbox" checked={a.switch_enabled} onChange={(e) => save({ switch_enabled: e.target.checked })} className="mt-1" data-testid="auto-switch-toggle" />
        <span><b className="text-[var(--navy)]">Passaggio automatico il</b> <Input type="date" value={a.switch_date} onChange={(e) => save({ switch_date: e.target.value })} className="inline-block w-40 h-8 mx-1" data-testid="auto-switch-date" /> <b className="text-[var(--navy)]">alle 23:59</b><br />
          <span className="text-xs text-slate-600">Alle 22:30 parte da sola la copia finale, alle 23:59 la web app passa al proprio database. Ti arriva un'email con l'esito; se qualcosa manca il passaggio non avviene e l'email ti dice cosa fare.</span></span>
      </label>
      {a.last_result && <p className={`text-xs ${a.last_result.ok ? "text-emerald-700" : "text-[var(--red)]"}`} data-testid="auto-switch-result">{fmtDate(a.last_result.at, true)} · {a.last_result.text}</p>}
      <label className="flex items-start gap-3 text-sm cursor-pointer">
        <input type="checkbox" checked={a.reminder_enabled} onChange={(e) => save({ reminder_enabled: e.target.checked })} className="mt-1" data-testid="auto-reminder-toggle" />
        <span><b className="text-[var(--navy)]">Promemoria password ogni giorno alle 9:00</b><br />
          <span className="text-xs text-slate-600">Rimanda l'email "scegli la password" a chi non l'ha ancora scelta.{a.reminder_last ? ` Ultimo invio: ${fmtDate(a.reminder_last.at, true)}, ${a.reminder_last.sent} email.` : ""}</span></span>
      </label>
      {a.switch_enabled && <p className="text-xs text-slate-500">Passaggio programmato: {g}/{m}/{y} alle 23:59.</p>}
    </div>
  );
}

function ApologyBox() {
  const [a, setA] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api("/web/invites/apology").then(setA).catch(() => setA(false)); }, []);
  if (!a) return null;
  const pending = a.users.filter((u) => !u.ready);
  const send = async () => {
    if (!window.confirm(`Inviare l'email di scuse con il nuovo link a ${pending.length} dipendenti?`)) return;
    setBusy(true);
    try { const r = await api("/web/invites/apology", { method: "POST" }); toast.success(`Email di scuse inviata a ${r.sent} dipendenti`); if (r.failed?.length) toast.error(`Non inviate: ${r.failed.join("; ")}`); setA({ ...a, sent_at: r.sent ? new Date().toISOString() : null, sent: r.sent, failed: r.failed }); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 space-y-2" data-testid="apology-box">
      <p className="font-semibold text-sm text-[var(--navy)]">Email di scuse ai 10 dipendenti con il link non valido</p>
      <p className="text-xs text-slate-600">{a.users.map((u) => `${u.nome}${u.ready ? " (password già scelta)" : ""}`).join(", ")}</p>
      {a.sent_at ? <p className="text-xs text-emerald-700" data-testid="apology-status">Inviata il {fmtDate(a.sent_at, true)} a {a.sent} dipendenti{a.failed?.length ? ` · non inviate: ${a.failed.join("; ")}` : ""}</p>
        : <Button className="rounded-full bg-[var(--navy)]" disabled={busy || !pending.length} onClick={send} data-testid="apology-send">{busy ? "Invio in corso..." : `Invia scuse e nuovo link (${pending.length})`}</Button>}
    </div>
  );
}

function NoticeBox() {
  const [n, setN] = useState(null);
  const [busy, setBusy] = useState("");
  useEffect(() => { api("/web/switch-notice").then(setN).catch(() => setN({ data: "2026-10-25", ora: "23:59" })); }, []);
  if (!n) return null;
  const send = async (soloAMe) => {
    const [y, m, g] = n.data.split("-");
    if (!soloAMe && !window.confirm(`Inviare a tutto il personale l'avviso del passaggio del ${g}/${m}/${y} alle ${n.ora}?`)) return;
    setBusy(soloAMe ? "me" : "all");
    try {
      const r = await api("/web/switch-notice/send", { method: "POST", body: { data: n.data, ora: n.ora, solo_a_me: soloAMe } });
      toast.success(soloAMe ? "Email di prova inviata a te" : `Avviso inviato a ${r.sent} dipendenti su ${r.total}`);
      if (r.failed?.length) toast.error(`Non inviate: ${r.failed.length}`);
      if (!soloAMe) setN({ ...n, sent_at: new Date().toISOString(), sent: r.sent, failed: r.failed });
    } catch (e) { toast.error(e.message); } finally { setBusy(""); }
  };
  return (
    <div className="rounded-xl bg-white border p-4 space-y-3" data-testid="switch-notice">
      <p className="font-semibold text-sm text-[var(--navy)]">Avviso del passaggio a tutto il personale</p>
      <p className="text-xs text-slate-600">Email con giorno e ora del passaggio e come rientrare con la propria password (a chi non l'ha ancora scelta ricorda l'email "scegli la password").</p>
      <div className="flex flex-wrap gap-3 items-center">
        <Input type="date" value={n.data} onChange={(e) => setN({ ...n, data: e.target.value })} className="w-44" data-testid="switch-notice-date" />
        <Input type="time" value={n.ora} onChange={(e) => setN({ ...n, ora: e.target.value })} className="w-32" data-testid="switch-notice-time" />
        <Button variant="outline" className="rounded-full" disabled={!!busy} onClick={() => send(true)} data-testid="switch-notice-test">{busy === "me" ? "Invio..." : "Prova solo a me"}</Button>
        <Button className="rounded-full bg-[var(--teal)]" disabled={!!busy} onClick={() => send(false)} data-testid="switch-notice-send">{busy === "all" ? "Invio in corso..." : "Invia avviso a tutti"}</Button>
      </div>
      {n.sent_at && <p className="text-xs text-emerald-700" data-testid="switch-notice-status">Ultimo invio: {fmtDate(n.sent_at, true)} · {n.sent} email inviate{n.failed?.length ? ` · ${n.failed.length} non inviate` : ""}</p>}
    </div>
  );
}

export function SwitchPanel() {
  const [m, setM] = useState(null);
  const [c, setC] = useState(null);
  const [word, setWord] = useState("");
  const [busy, setBusy] = useState(false);
  const load = useCallback(async () => {
    setM(await api("/web/mode").catch(() => null));
    setC(await api("/web/mode/checklist").catch(() => null));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!m) return null;
  const local = m.mode === "local";
  const go = async (mode) => {
    setBusy(true);
    try { await api("/web/mode", { method: "POST", body: { mode, conferma: word } }); toast.success(mode === "local" ? "Passaggio completato: esci e rientra con la tua password" : "La web app usa di nuovo l'app mobile"); setWord(""); setTimeout(() => window.location.reload(), 1500); }
    catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <Panel className={`mb-6 space-y-3 ${local ? "!border-emerald-300 !bg-emerald-50" : ""}`} data-testid="switch-panel">
      <div className="flex items-center gap-3"><Power className="w-5 h-5 text-[var(--teal)]" /><h2 className="font-display font-bold text-lg text-[var(--navy)]">Commutatore: passaggio alla web app indipendente</h2></div>
      <p className="text-sm font-semibold text-[var(--navy)]" data-testid="switch-mode">{local ? `Attivo: la web app usa il proprio database (dal ${fmtDate(m.switched_at, true)}, ${m.switched_by || ""})` : "Spento: la web app usa ancora i dati dell'app mobile"}</p>
      {!local && c && (
        <ul className="text-sm text-slate-600 list-disc ml-5" data-testid="switch-checklist">
          <li>Ultima copia completa: {c.last_import?.finished_at ? `${fmtDate(c.last_import.finished_at, true)} (${c.last_import.status === "done" ? "completata" : "non completata"})` : "mai eseguita"} {c.import_fresh ? "· ok" : "· da rifare (serve una copia completata nelle ultime 24 ore)"}</li>
          <li>Dipendenti con password della web app: {c.with_password} su {c.users}. Chi non l'ha scelta potrà usare l'email "prima password" anche dopo il passaggio.</li>
          <li>Dopo il passaggio tutti devono rientrare con la propria password. L'app mobile non riceverà più i nuovi dati.</li>
        </ul>)}
      <ApologyBox />
      {!local && <AutoBox />}
      {!local && <NoticeBox />}
      <p className="text-sm text-slate-600">{local ? "Per tornare all'app mobile" : "Per accendere"} scrivi <b>PASSAGGIO</b> e conferma.{local ? " I dati inseriti dopo il passaggio restano solo nella web app." : ""}</p>
      <div className="flex flex-wrap gap-3">
        <Input value={word} onChange={(e) => setWord(e.target.value)} placeholder="PASSAGGIO" className="max-w-[200px] bg-white" data-testid="switch-confirm-input" />
        <Button onClick={() => go(local ? "upstream" : "local")} disabled={busy || word !== "PASSAGGIO"} className={`rounded-full ${local ? "bg-slate-600" : "bg-[var(--navy)]"}`} data-testid="switch-button">{busy ? "Attendi..." : local ? "Torna all'app mobile" : "Esegui il passaggio"}</Button>
      </div>
    </Panel>
  );
}
